/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialAnalysisInput/components/EmbodiedCarbonFactorCard.tsx.
 */

import React, { useMemo, useState } from "react";
import type {
  MaterialAnalysisSubmission,
  SubmissionSummary,
  EmbodiedCarbonFactorStats,
} from "../../../../../services/materialAnalysisService";
import {
  type RedDotKind,
  redDotHoverSuffix,
  redDotLegendText,
  embodiedCarbonRangeLegendText,
  resolveRedDotKind,
} from "../utils/embodiedCarbonFactorLegend";

/** t-value for 95% confidence interval (two-tailed, alpha=0.05) by degrees of freedom */
function tValue95(df: number): number {
  const table: Record<number, number> = {
    1: 12.71, 2: 4.30, 3: 3.18, 4: 2.78, 5: 2.57, 6: 2.45, 7: 2.36, 8: 2.31,
    9: 2.26, 10: 2.23, 11: 2.2, 12: 2.18, 13: 2.16, 14: 2.14, 15: 2.13,
    16: 2.12, 17: 2.11, 18: 2.1, 19: 2.09, 20: 2.09, 25: 2.06, 30: 2.04,
  };
  if (df <= 30) return table[df] ?? (df <= 20 ? 2.09 : 2.04);
  return 1.96;
}

interface EmbodiedCarbonFactorCardProps {
  hasData?: boolean;
  materialSelected?: boolean;
  submissions?: MaterialAnalysisSubmission[];
  embodiedCarbonFactor?: EmbodiedCarbonFactorStats | null;
  materialLabel?: string;
  materialName?: string;
  subMaterialName?: string;
  dataSpecificityApplied?: boolean;
}

/** Exported const — Embodied Carbon Factor Card. */
export const EmbodiedCarbonFactorCard: React.FC<EmbodiedCarbonFactorCardProps> = ({
  hasData = false,
  materialSelected = false,
  submissions = [],
  embodiedCarbonFactor,
  materialLabel = "Material",
  materialName,
  subMaterialName,
  dataSpecificityApplied = false,
}) => {
  const displayMaterialName = materialName ?? materialLabel;
  const DISPLAY_UNIT = "kg CO₂eq/kg";
  const [hoveredDot, setHoveredDot] = useState<{
    value: number;
    leftPct: number;
    topPx: number;
    label?: string;
  } | null>(null);

  const carbonPoints = useMemo(() => {
    const pickSummary = (s: MaterialAnalysisSubmission): SubmissionSummary | null => {
      return s.a1a3Summary || s.a3Summary || s.a2Summary || s.a1Summary || null;
    };

    const toNumber = (v: unknown) => {
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const classify = (s: MaterialAnalysisSubmission): "defined" | "mixed" => {
      const raw =
        (s as any).specificityType ??
        (s as any).specificityClass ??
        (s as any).specificity ??
        (s as any).specificity_category ??
        null;
      const normalized = String(raw || "").toLowerCase();
      if (normalized.includes("defined")) return "defined";
      if (normalized.includes("mixed")) return "mixed";
      // If backend doesn't provide the class yet, default to mixed.
      return "mixed";
    };

    return (submissions || [])
      .map((s) => {
        const summary = pickSummary(s);
        const value = toNumber(summary?.embodiedCarbonValue);
        if (value === null) return null;
        return {
          id: s.submissionId || s.submissionCode,
          value,
          unit: summary?.embodiedCarbonUnit || DISPLAY_UNIT,
          type: classify(s),
        };
      })
      .filter(Boolean) as Array<{ id: string; value: number; unit: string; type: "defined" | "mixed" }>;
  }, [submissions]);

  const stats = useMemo(() => {
    if (embodiedCarbonFactor && embodiedCarbonFactor.sampleSize > 0) {
      const count = embodiedCarbonFactor.sampleSize;
      return {
        count,
        min: embodiedCarbonFactor.min,
        max: embodiedCarbonFactor.max,
        recommended: embodiedCarbonFactor.redDotValue,
        unit: embodiedCarbonFactor.unit || DISPLAY_UNIT,
        redDotKind: resolveRedDotKind(count, embodiedCarbonFactor.method),
      };
    }
    if (!carbonPoints.length) {
      return {
        count: 0,
        min: null as number | null,
        max: null as number | null,
        recommended: null as number | null,
        unit: DISPLAY_UNIT,
        redDotKind: "max" as RedDotKind,
      };
    }
    const values = carbonPoints.map((p) => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const n = values.length;

    let recommended: number;
    if (n <= 5) {
      recommended = max;
    } else {
      const mean = values.reduce((a, b) => a + b, 0) / n;
      const sumSq = values.reduce((acc, v) => acc + (v - mean) ** 2, 0);
      const s = Math.sqrt(sumSq / (n - 1));
      const t = tValue95(n - 1);
      const se = s / Math.sqrt(n);
      recommended = mean + t * se;
    }

    const unit = carbonPoints.find((p) => p.value === max)?.unit || carbonPoints[0].unit || DISPLAY_UNIT;
    return {
      count: n,
      min,
      max,
      recommended,
      unit,
      redDotKind: resolveRedDotKind(n),
    };
  }, [carbonPoints, embodiedCarbonFactor]);

  const PLOT_INSET_PCT = 4; // matches CSS baseline insets
  const PLOT_USABLE_WIDTH_PCT = 100 - 2 * PLOT_INSET_PCT;
  const MIN_DISTINCT_GAP_PCT = 5; // min horizontal gap between distinct values (% of plot)
  const DOT_SIZE_PX = 14;
  const HIGHLIGHT_DOT_SIZE_PX = 18;
  const BASELINE_TOP_PCT = 55;

  const axis = useMemo(() => {
    // Add extra headroom on x-axis: small values (≤2) round to 0.5 steps; large values add ~15% buffer and round to nice numbers.
    // Last tick on axis is always an integer.
    const dataMin = stats.min ?? 0;
    const max = Math.max(stats.max ?? 0, stats.recommended ?? 0);
    let domainMin = 0;
    let rawMax: number;
    if (max <= 0) {
      rawMax = 1.6;
    } else {
      const rawCeil = Math.ceil(max * 10) / 10;
      if (rawCeil <= 2) {
        const nextHalf = Math.ceil(rawCeil * 2) / 2;
        rawMax = Math.max(1.5, nextHalf);
      } else {
        const withBuffer = rawCeil * 1.15;
        const magnitude = Math.pow(10, Math.floor(Math.log10(withBuffer)));
        const normalized = withBuffer / magnitude;
        const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
        rawMax = nice * magnitude;
      }
    }
    let domainMax = Math.ceil(rawMax);
    const tickCandidates = [0, 0.5, 1.0, 1.5, 2.0];
    let ticks: number[];
    if (domainMax <= 2) {
      ticks = tickCandidates.filter((t) => t <= domainMax);
      if (ticks.length > 0 && ticks[ticks.length - 1] !== domainMax) ticks = [...ticks, domainMax];
    } else {
      const mag = Math.pow(10, Math.floor(Math.log10(domainMax)));
      const step = domainMax <= 2 * mag ? mag : domainMax <= 5 * mag ? mag * 2 : mag * 5;
      ticks = [];
      for (let t = 0; t <= domainMax; t += step) ticks.push(t);
      if (ticks.length > 0 && ticks[ticks.length - 1] !== domainMax) ticks.push(domainMax);
    }

    const plotValues = [
      ...carbonPoints.map((p) => Number(p.value.toFixed(3))),
      ...(stats.recommended !== null ? [Number(stats.recommended.toFixed(3))] : []),
    ];
    const distinctValues = Array.from(new Set(plotValues)).sort((a, b) => a - b);

    if (distinctValues.length >= 2) {
      let smallestGap = Infinity;
      for (let i = 1; i < distinctValues.length; i += 1) {
        smallestGap = Math.min(smallestGap, distinctValues[i] - distinctValues[i - 1]);
      }

      if (smallestGap > 0) {
        const defaultSpan = domainMax - domainMin;
        const gapPct = (smallestGap / defaultSpan) * PLOT_USABLE_WIDTH_PCT;

        if (gapPct < MIN_DISTINCT_GAP_PCT) {
          const targetSpan = (smallestGap * PLOT_USABLE_WIDTH_PCT) / MIN_DISTINCT_GAP_PCT;
          const padding = Math.max(smallestGap, targetSpan * 0.12);
          let zoomMin = Math.max(0, dataMin - padding);
          let zoomMax = max + padding;

          if (zoomMax - zoomMin < targetSpan) {
            const extra = (targetSpan - (zoomMax - zoomMin)) / 2;
            zoomMin = Math.max(0, zoomMin - extra);
            zoomMax += extra;
          }

          const zoomSpan = zoomMax - zoomMin;
          let step: number;
          if (zoomSpan <= 0.15) step = 0.02;
          else if (zoomSpan <= 0.35) step = 0.05;
          else if (zoomSpan <= 0.75) step = 0.1;
          else if (zoomSpan <= 1.5) step = 0.25;
          else step = 0.5;

          zoomMin = Math.floor(zoomMin / step) * step;
          zoomMax = Math.ceil(zoomMax / step) * step;

          if (zoomMax - zoomMin < defaultSpan * 0.9) {
            domainMin = zoomMin;
            domainMax = zoomMax;
            ticks = [];
            for (let t = zoomMin; t <= zoomMax + step / 2; t += step) {
              ticks.push(Number(t.toFixed(2)));
            }
            if (ticks.length === 0 || ticks[ticks.length - 1] !== Number(zoomMax.toFixed(2))) {
              ticks.push(Number(zoomMax.toFixed(2)));
            }
          }
        }
      }
    }

    return { min: domainMin, max: domainMax, ticks };
  }, [stats.min, stats.max, stats.recommended, carbonPoints]);

  const toLeftPct = (value: number) => {
    const denom = axis.max - axis.min || 1;
    const clamped = Math.max(axis.min, Math.min(axis.max, value));
    const p = (clamped - axis.min) / denom;
    return PLOT_INSET_PCT + p * (100 - 2 * PLOT_INSET_PCT);
  };

  const valueStackKey = (value: number) => value.toFixed(3);

  const resolveDotStackGroup = (
    point: { type: "defined" | "mixed" },
    isHighlight: boolean
  ): "defined" | "mixed" | "highlight" => {
    if (isHighlight) return "highlight";
    if (dataSpecificityApplied || point.type === "defined") return "defined";
    return "mixed";
  };

  const dotLayouts = useMemo(() => {
    type LayoutPoint = {
      id: string;
      value: number;
      unit: string;
      type: "defined" | "mixed";
      isHighlight: boolean;
      stackGroup: "defined" | "mixed" | "highlight";
      leftPct: number;
      topOffsetPx: number;
    };

    const buckets = new Map<string, LayoutPoint[]>();

    for (const point of carbonPoints) {
      const isHighlight =
        stats.recommended !== null &&
        stats.count <= 5 &&
        Math.abs(point.value - stats.recommended) < 1e-9;
      const stackGroup = resolveDotStackGroup(point, isHighlight);
      const bucketKey = `${valueStackKey(point.value)}|${stackGroup}`;
      const entry: LayoutPoint = {
        id: point.id,
        value: point.value,
        unit: point.unit,
        type: point.type,
        isHighlight,
        stackGroup,
        leftPct: toLeftPct(point.value),
        topOffsetPx: 0,
      };
      const bucket = buckets.get(bucketKey);
      if (bucket) bucket.push(entry);
      else buckets.set(bucketKey, [entry]);
    }

    const layouts: LayoutPoint[] = [];
    Array.from(buckets.values()).forEach((bucket) => {
      const ordered = [...bucket].sort((a, b) => a.id.localeCompare(b.id));
      ordered.forEach((entry, index) => {
        const stackStepPx = entry.isHighlight ? HIGHLIGHT_DOT_SIZE_PX : DOT_SIZE_PX;
        layouts.push({
          ...entry,
          topOffsetPx: -index * stackStepPx,
        });
      });
    });

    return layouts;
  }, [
    carbonPoints,
    stats.recommended,
    stats.count,
    axis.min,
    axis.max,
    dataSpecificityApplied,
  ]);

  const highlightPosition = useMemo(() => {
    if (stats.recommended === null) return null;
    const leftPct = toLeftPct(stats.recommended);
    // If the value is close to edges, change anchor so the bubble stays inside.
    // Thresholds are in % of plot width.
    const anchor: "left" | "center" | "right" =
      leftPct >= 90 ? "right" : leftPct <= 10 ? "left" : "center";
    const transform =
      anchor === "right"
        ? "translate(-100%, -50%)"
        : anchor === "left"
        ? "translate(0%, -50%)"
        : "translate(-50%, -50%)";
    return { leftPct, transform };
  }, [stats.recommended, axis.max, axis.min]);

  if (!hasData) {
    return (
      <section className="analysis-card analysis-scatter-card">
        <header className="analysis-card__header">
          <h3>Representative Embodied Carbon Factor</h3>
        </header>
        <div className="scatter-placeholder">
          <p>
            {materialSelected
              ? dataSpecificityApplied
                ? "No datasets match your data specificity criteria. Please relax your input conditions or select a different material."
                : "no data available, please relax your input conditions or select a different material"
              : "No data available. Please select a material to view the representative embodied carbon factor."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="analysis-card analysis-scatter-card">
      <header className="analysis-card__header">
        <h3>Representative Embodied Carbon Factor</h3>
      </header>

      <p className="scatter-summary">
        The representative embodied carbon factor for {materialLabel} is estimated at{" "}
        <strong className="scatter-summary__value">
          {(stats.recommended ?? 0).toFixed(3)} {DISPLAY_UNIT}
        </strong>
        , based on {stats.count} dataset{stats.count === 1 ? "" : "s"}.
      </p>

      <div className="scatter-body">
        <div className="scatter-chart">
          <div className="scatter-chart-row">
            <div className="scatter-y-label">
              <span>
                <span className="scatter-y-label__line">Material - {displayMaterialName}</span>
                {subMaterialName && (
                  <span className="scatter-y-label__line scatter-y-label__material">{subMaterialName}</span>
                )}
              </span>
            </div>

            <div className="scatter-plot" aria-label="Embodied carbon factor plot">
              <div className="scatter-y-axis-line" aria-hidden="true" />
              <div className="scatter-baseline" />

              {/* Min to Upper Bound (red dot) Range */}
              {stats.min !== null && stats.recommended !== null && (
                <div
                  className="scatter-minmax"
                  style={{
                    left: `${toLeftPct(stats.min)}%`,
                    width: `${Math.max(0.5, toLeftPct(stats.recommended) - toLeftPct(stats.min))}%`,
                  }}
                  aria-hidden="true"
                >
                  <div className="scatter-minmax__line" />
                  <div className="scatter-minmax__cap scatter-minmax__cap--left" />
                  <div className="scatter-minmax__cap scatter-minmax__cap--right" />
                </div>
              )}

              {/* Dots */}
              {dotLayouts.map((p) => {
                const className = [
                  "scatter-dot",
                  dataSpecificityApplied || p.type === "defined"
                    ? "scatter-dot--defined"
                    : "scatter-dot--mixed",
                  p.isHighlight ? "scatter-dot--highlight" : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <div
                    key={p.id}
                    className={className}
                    style={{
                      left: `${p.leftPct}%`,
                      top: `calc(${BASELINE_TOP_PCT}% + ${p.topOffsetPx}px)`,
                    }}
                    onMouseEnter={() => {
                      const base = `${p.value.toFixed(3)} ${DISPLAY_UNIT}`;
                      const label = p.isHighlight
                        ? `${base} (${redDotHoverSuffix(stats.redDotKind)})`
                        : base;
                      setHoveredDot({
                        value: p.value,
                        leftPct: p.leftPct,
                        topPx: p.topOffsetPx,
                        label,
                      });
                    }}
                    onMouseLeave={() => setHoveredDot(null)}
                    title={
                      p.isHighlight
                        ? `${p.value.toFixed(3)} ${DISPLAY_UNIT} (${redDotHoverSuffix(stats.redDotKind)})`
                        : `${p.value.toFixed(3)} ${DISPLAY_UNIT}`
                    }
                    aria-label={
                      p.isHighlight
                        ? `${p.value.toFixed(3)} ${DISPLAY_UNIT} (${redDotHoverSuffix(stats.redDotKind)})`
                        : `${p.value.toFixed(3)} ${DISPLAY_UNIT}`
                    }
                  />
                );
              })}
              {/* Red dot at CI upper bound when it doesn't match any data point (n > 5) */}
              {stats.recommended !== null &&
                stats.count > 5 &&
                !carbonPoints.some((p) => Math.abs(p.value - stats.recommended!) < 1e-9) && (
                  <div
                    className="scatter-dot scatter-dot--highlight"
                    style={{
                      left: `${toLeftPct(stats.recommended)}%`,
                      top: `${BASELINE_TOP_PCT}%`,
                    }}
                    onMouseEnter={() => {
                      const rec = stats.recommended!;
                      const suffix = redDotHoverSuffix(stats.redDotKind);
                      setHoveredDot({
                        value: rec,
                        leftPct: toLeftPct(rec),
                        topPx: 0,
                        label: `${rec.toFixed(3)} ${DISPLAY_UNIT} (${suffix})`,
                      });
                    }}
                    onMouseLeave={() => setHoveredDot(null)}
                    title={`${stats.recommended.toFixed(3)} ${DISPLAY_UNIT} (${redDotHoverSuffix(stats.redDotKind)})`}
                    aria-label={`${stats.recommended.toFixed(3)} ${DISPLAY_UNIT} (${redDotHoverSuffix(stats.redDotKind)})`}
                  />
                )}

              {/* Hover tooltip */}
              {hoveredDot && (
                <div
                  className="scatter-dot-tooltip"
                  style={{
                    left: `${hoveredDot.leftPct}%`,
                    top: `calc(${BASELINE_TOP_PCT}% + ${hoveredDot.topPx}px)`,
                    transform: "translate(-50%, -120%)",
                  }}
                >
                  {hoveredDot.label}
                </div>
              )}

              {/* Dotted line from value label to red dot */}
              {highlightPosition && stats.recommended !== null && (
                <div
                  className="scatter-highlight-line"
                  style={{
                    left: `${highlightPosition.leftPct}%`,
                  }}
                  aria-hidden="true"
                />
              )}

              {/* Highlight label */}
              {highlightPosition && stats.recommended !== null && (
                <div
                  className="scatter-highlight"
                  style={{
                    left: `${highlightPosition.leftPct}%`,
                    transform: highlightPosition.transform,
                  }}
                >
                  <div className="scatter-highlight__value">{stats.recommended.toFixed(3)}</div>
                </div>
              )}
            </div>
          </div>

          <div className="scatter-axis">
            <div className="scatter-axis__spacer" aria-hidden="true" />
            <div className="scatter-axis__content">
              <div className="scatter-axis__line" aria-hidden="true" />
              <div className="scatter-axis__ticks" aria-hidden="true">
                {axis.ticks.map((t) => (
                  <span key={t}>
                    <span className="scatter-axis__tick-mark" />
                    {t.toFixed(2)}
                  </span>
                ))}
              </div>
              <div className="scatter-axis__label">
                Cradle to Gate Embodied Carbon (kg CO<sub>2</sub>eq/ kg)
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="scatter-legend">
        {stats.recommended !== null && (
          <div className="scatter-legend__item">
            <span className="legend-dot legend-dot--highlight" />
            {redDotLegendText(stats.redDotKind)}
          </div>
        )}
        {dataSpecificityApplied && carbonPoints.length > 0 && (
          <div className="scatter-legend__item">
            <span className="legend-dot legend-dot--defined" />
            Data Specificity datasets meeting input conditions
          </div>
        )}
        {!dataSpecificityApplied && carbonPoints.some((p) => p.type === "defined") && (
          <div className="scatter-legend__item">
            <span className="legend-dot legend-dot--defined" />
            Defined-specificity datasets meeting input conditions
          </div>
        )}
        {!dataSpecificityApplied && carbonPoints.some((p) => p.type === "mixed") && (
          <div className="scatter-legend__item">
            <span className="legend-dot legend-dot--mixed" />
            Mixed-specificity datasets meeting input conditions
          </div>
        )}
        {stats.min !== null && stats.recommended !== null && (
          <div className="scatter-legend__item">
            <span className="legend-range" aria-hidden="true" />
            {embodiedCarbonRangeLegendText(stats.count)}
          </div>
        )}
      </div>
    </section>
  );
};

